import React, { useState, useMemo } from 'react';
import {
  MapPin, Package, Save, RotateCcw, ChevronDown, ChevronUp,
  CheckCircle, AlertTriangle, Copy, Loader, Info, Star, Hash, Layers, X
} from 'lucide-react';
import { FieldRegistrationLocation, EditableBinProperties, EditableItemCapacity, FieldRegistrationItem } from '../../../../types/fieldRegistration';
import { StcBinDto } from '../../../../types/stcBin';
import { StcBinGrpDto } from '../../../../services/stcBinGrpService';
import { StcMovementTypeDto } from '../../../../services/stcMovementTypeService';
import { fieldRegistrationService } from '../../../../services/fieldRegistrationService';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface LocationDetailProps {
  location: FieldRegistrationLocation;
  saving: boolean;
  binGroups: StcBinGrpDto[];
  movementTypes: StcMovementTypeDto[];
  allBins: StcBinDto[];
  onUpdateBinProperties: (properties: EditableBinProperties) => Promise<boolean>;
  onUpdateItemCapacity: (
    itemNo: string,
    binSizeNo: number,
    existingId: number | null,
    capacity: EditableItemCapacity
  ) => Promise<boolean>;
  onUpdateItemStdBinCode: (itemNo: string, binCode: string) => Promise<boolean>;
  onBulkApplyCapacity: (
    items: FieldRegistrationItem[],
    binSizeNo: number,
    capacity: EditableItemCapacity,
    applyToMissingOnly: boolean
  ) => Promise<{ success: number; failed: number }>;
}

export const LocationDetail: React.FC<LocationDetailProps> = ({
  location,
  saving,
  binGroups,
  movementTypes,
  allBins,
  onUpdateBinProperties,
  onUpdateItemCapacity,
  onUpdateItemStdBinCode,
  onBulkApplyCapacity,
}) => {
  const { t } = useTranslation();
  const { bin, binType, binSize, items, overrides, completionStatus } = location;

  // Bin properties editing state
  const [binEditing, setBinEditing] = useState(false);
  const [binProps, setBinProps] = useState<EditableBinProperties>({
    pickPriorityNo: bin.pickPriorityNo,
    putAwayPriorityNo: bin.putAwayPriorityNo,
    sharedBinYesNo: bin.sharedBinYesNo,
    autoReplenishment: bin.autoReplenishment,
    replenishMinLevel: bin.replenishMinLevel,
    replenishMaxLevel: bin.replenishMaxLevel,
    pickableYesNo: bin.pickableYesNo,
    reservationAllowed: bin.reservationAllowed,
    // New fields
    itemNo: bin.itemNo || '',
    sharedBinMaxQuantity: bin.sharedBinMaxQuantity,
    movementTypeNo: bin.movementTypeNo,
    customSortID: bin.customSortID,
    binGrpNo: bin.binGrpNo,
  });

  // Item search for dedicated item (fast vare) picker
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [showItemPicker, setShowItemPicker] = useState(false);

  // Items from all bins for picking dedicated item
  const uniqueItemsFromBins = useMemo(() => {
    const itemMap = new Map<string, string>();
    // Gather items from items in this location first
    items.forEach(item => {
      itemMap.set(item.itemNo, item.description1);
    });
    return Array.from(itemMap.entries()).map(([itemNo, desc]) => ({ itemNo, description: desc }));
  }, [items]);

  // Item capacity editing state
  const [editingItemNo, setEditingItemNo] = useState<string | null>(null);
  const [editCapacity, setEditCapacity] = useState<EditableItemCapacity>({ maxQuantity: 0, putAwayPriorityNo: 0 });

  // Section expand state
  const [binPropsExpanded, setBinPropsExpanded] = useState(true);
  const [extendedPropsExpanded, setExtendedPropsExpanded] = useState(true);
  const [itemsExpanded, setItemsExpanded] = useState(true);

  // Effective values (COALESCE: bin ?? binType)
  const effectiveValues = useMemo(() => ({
    pickPriorityNo: fieldRegistrationService.getEffectiveValue(bin.pickPriorityNo, binType?.pickPriorityNo),
    putAwayPriorityNo: fieldRegistrationService.getEffectiveValue(bin.putAwayPriorityNo, binType?.putAwayPriorityNo),
    sharedBinYesNo: fieldRegistrationService.getEffectiveValue(bin.sharedBinYesNo, binType?.sharedBinYesNo),
    autoReplenishment: fieldRegistrationService.getEffectiveValue(bin.autoReplenishment, binType?.autoReplenishment),
    replenishMinLevel: fieldRegistrationService.getEffectiveValue(bin.replenishMinLevel, binType?.replenishMinLevel),
    replenishMaxLevel: fieldRegistrationService.getEffectiveValue(bin.replenishMaxLevel, binType?.replenishMaxLevel),
    pickableYesNo: fieldRegistrationService.getEffectiveValue(bin.pickableYesNo, binType?.pickableYesNo),
    reservationAllowed: fieldRegistrationService.getEffectiveValue(bin.reservationAllowed, binType?.reservationAllowed),
  }), [bin, binType]);

  // ── Helpers for new fields ───────────────────────────────────────

  const getBinGroupName = (grpNo: number | undefined | null): string => {
    if (grpNo == null) return t('warehouse.modules.fieldRegistration.detail.notSet', {}, 'Ikke satt');
    const grp = binGroups.find(g => g.binGrpNo === grpNo);
    return grp ? `${grp.description1} (${grp.binGrpNo})` : t('warehouse.modules.fieldRegistration.detail.groupFallback', { '0': grpNo }, `Gruppe ${grpNo}`);
  };

  const getMovementTypeName = (typeNo: number | undefined | null): string => {
    if (typeNo == null) return t('warehouse.modules.fieldRegistration.detail.notSet', {}, 'Ikke satt');
    const mt = movementTypes.find(m => m.movementTypeNo === typeNo);
    return mt ? `${mt.description1} (${mt.movementTypeNo})` : t('warehouse.modules.fieldRegistration.detail.typeFallback', { '0': typeNo }, `Type ${typeNo}`);
  };

  // ── Bin Properties Save ────────────────────────────────────────

  const handleSaveBinProps = async () => {
    const success = await onUpdateBinProperties(binProps);
    if (success) {
      setBinEditing(false);
    }
  };

  const handleResetBinProps = () => {
    setBinProps({
      pickPriorityNo: bin.pickPriorityNo,
      putAwayPriorityNo: bin.putAwayPriorityNo,
      sharedBinYesNo: bin.sharedBinYesNo,
      autoReplenishment: bin.autoReplenishment,
      replenishMinLevel: bin.replenishMinLevel,
      replenishMaxLevel: bin.replenishMaxLevel,
      pickableYesNo: bin.pickableYesNo,
      reservationAllowed: bin.reservationAllowed,
      itemNo: bin.itemNo || '',
      sharedBinMaxQuantity: bin.sharedBinMaxQuantity,
      movementTypeNo: bin.movementTypeNo,
      customSortID: bin.customSortID,
      binGrpNo: bin.binGrpNo,
    });
    setBinEditing(false);
    setShowItemPicker(false);
    setItemSearchTerm('');
  };

  // ── Item Capacity Save ─────────────────────────────────────────

  const handleStartEditItem = (item: FieldRegistrationItem) => {
    setEditingItemNo(item.itemNo);
    setEditCapacity({
      maxQuantity: item.itemBinSize?.maxQuantity || 0,
      putAwayPriorityNo: item.itemBinSize?.putAwayPriorityNo || 0,
    });
  };

  const handleSaveItemCapacity = async (item: FieldRegistrationItem) => {
    const success = await onUpdateItemCapacity(
      item.itemNo,
      bin.binSizeNo,
      item.itemBinSize?.id || null,
      editCapacity
    );
    if (success) {
      setEditingItemNo(null);
    }
  };

  const handleBulkApply = async () => {
    if (!editingItemNo) return;
    const result = await onBulkApplyCapacity(
      items,
      bin.binSizeNo,
      editCapacity,
      true // Only apply to items without config
    );
    if (result.success > 0) {
      setEditingItemNo(null);
    }
  };

  // ── StdBinCode Quick-action ──────────────────────────────────────

  const handleSetStdBinCode = async (itemNo: string) => {
    await onUpdateItemStdBinCode(itemNo, bin.binCode);
  };

  // ── Render Helpers ─────────────────────────────────────────────

  const renderFieldRow = (
    label: string,
    fieldKey: keyof typeof overrides,
    value: any,
    typeValue: any,
    isBoolean: boolean = false
  ) => {
    const isOverridden = overrides[fieldKey];
    const editing = binEditing;

    return (
      <div
        className={`flex items-center justify-between py-2 px-3 rounded-lg ${
          isOverridden ? 'bg-[#E0F2F4] border border-[#0E7C86]/20' : 'bg-gray-50'
        }`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-700">{label}</span>
            {isOverridden && (
              <span className="text-[10px] font-bold text-[#0E7C86] bg-[#0E7C86]/10 px-1.5 py-0.5 rounded">
                {t('warehouse.modules.fieldRegistration.detail.overridden', {}, 'OVERSTYRT')}
              </span>
            )}
            {!isOverridden && typeValue != null && (
              <span className="text-[10px] text-gray-400">
                {t('warehouse.modules.fieldRegistration.detail.inherited', { '0': isBoolean ? (typeValue ? t('warehouse.modules.fieldRegistration.detail.yes', {}, 'Ja') : t('warehouse.modules.fieldRegistration.detail.no', {}, 'Nei')) : typeValue }, `(arvet: ${isBoolean ? (typeValue ? 'Ja' : 'Nei') : typeValue})`)}
              </span>
            )}
          </div>
        </div>

        <div className="flex-shrink-0 ml-2">
          {editing ? (
            isBoolean ? (
              <button
                onClick={() => setBinProps(prev => ({ ...prev, [fieldKey]: !prev[fieldKey as keyof EditableBinProperties] }))}
                className={`px-3 py-1 rounded text-xs font-medium min-h-[32px] min-w-[48px] ${
                  binProps[fieldKey as keyof EditableBinProperties]
                    ? 'bg-[#0E7C86] text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}
              >
                {binProps[fieldKey as keyof EditableBinProperties] ? t('warehouse.modules.fieldRegistration.detail.yes', {}, 'Ja') : t('warehouse.modules.fieldRegistration.detail.no', {}, 'Nei')}
              </button>
            ) : (
              <input
                type="number"
                value={binProps[fieldKey as keyof EditableBinProperties] as number ?? ''}
                onChange={(e) => setBinProps(prev => ({
                  ...prev,
                  [fieldKey]: e.target.value ? Number(e.target.value) : undefined
                }))}
                className="w-20 px-2 py-1 border border-gray-300 rounded text-xs text-right focus:border-[#0E7C86] focus:ring-1 focus:ring-[#0E7C86] min-h-[32px]"
              />
            )
          ) : (
            <span className={`text-sm font-medium ${isOverridden ? 'text-[#0E7C86]' : 'text-gray-600'}`}>
              {isBoolean
                ? (value != null ? (value ? t('warehouse.modules.fieldRegistration.detail.yes', {}, 'Ja') : t('warehouse.modules.fieldRegistration.detail.no', {}, 'Nei')) : '-')
                : (value ?? '-')
              }
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Location Header Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <MapPin size={18} className="text-[#0E7C86]" />
              <h2 className="text-lg font-bold text-gray-900">{bin.binCode}</h2>
            </div>
            <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
              <span>{binType?.description1 || t('warehouse.modules.fieldRegistration.detail.typeFallback', { '0': bin.binTypeNo }, `Type ${bin.binTypeNo}`)}</span>
              <span>|</span>
              <span>{binSize?.description1 || t('warehouse.modules.fieldRegistration.detail.sizeFallback', { '0': bin.binSizeNo }, `Str. ${bin.binSizeNo}`)}</span>
              {bin.stcZoneID && (
                <>
                  <span>|</span>
                  <span>{t('warehouse.modules.fieldRegistration.zoneLabel', { '0': bin.stcZoneID }, `Sone ${bin.stcZoneID}`)}</span>
                </>
              )}
            </div>
          </div>

          {/* Completion Badge */}
          <div className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
            completionStatus.completionPercent >= 100
              ? 'bg-green-100 text-green-700'
              : completionStatus.completionPercent > 0
                ? 'bg-amber-100 text-amber-700'
                : 'bg-gray-100 text-gray-600'
          }`}>
            {completionStatus.completionPercent >= 100 ? (
              <CheckCircle size={12} />
            ) : (
              <AlertTriangle size={12} />
            )}
            {completionStatus.completionPercent}%
          </div>
        </div>

        {/* Mini progress */}
        <div className="w-full bg-gray-200 rounded-full h-1.5 mt-3">
          <div
            className={`h-1.5 rounded-full transition-all duration-300 ${
              completionStatus.completionPercent >= 100 ? 'bg-green-500' : 'bg-[#0E7C86]'
            }`}
            style={{ width: `${completionStatus.completionPercent}%` }}
          />
        </div>

        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
          <span>{t('warehouse.modules.fieldRegistration.detail.itemsCount', { '0': completionStatus.totalItems }, `${completionStatus.totalItems} varer`)}</span>
          <span>{t('warehouse.modules.fieldRegistration.detail.withCapacity', { '0': completionStatus.itemsWithCapacity }, `${completionStatus.itemsWithCapacity} m/kapasitet`)}</span>
          <span>{t('warehouse.modules.fieldRegistration.detail.missing', { '0': completionStatus.totalItems - completionStatus.itemsWithCapacity }, `${completionStatus.totalItems - completionStatus.itemsWithCapacity} mangler`)}</span>
        </div>
      </div>

      {/* ── Bin Properties Section (Original 8 fields) ──────────── */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <button
          onClick={() => setBinPropsExpanded(!binPropsExpanded)}
          className="w-full flex items-center justify-between p-4 text-left min-h-[44px]"
        >
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-gray-900">{t('warehouse.modules.fieldRegistration.detail.binPropertiesTitle', {}, 'Lokasjonsegenskaper')}</h3>
            {Object.values(overrides).some(v => v) && (
              <span className="text-[10px] font-bold text-[#0E7C86] bg-[#E0F2F4] px-1.5 py-0.5 rounded">
                {t('warehouse.modules.fieldRegistration.detail.customized', {}, 'TILPASSET')}
              </span>
            )}
          </div>
          {binPropsExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
        </button>

        {binPropsExpanded && (
          <div className="px-4 pb-4 space-y-2">
            {/* Info banner */}
            <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg p-2.5 mb-2">
              <Info size={14} className="text-blue-500 flex-shrink-0 mt-0.5" />
              <p className="text-[10px] text-blue-700">
                {t('warehouse.modules.fieldRegistration.detail.bannerPrefix', {}, 'Egenskaper med')} <span className="font-bold text-[#0E7C86]">{t('warehouse.modules.fieldRegistration.detail.overridden', {}, 'OVERSTYRT')}</span> {t('warehouse.modules.fieldRegistration.detail.bannerMiddle', {}, 'er tilpasset for denne lokasjonen. Andre arves fra lokasjonstype')} <span className="font-medium">{binType?.description1 || bin.binTypeNo}</span>.
              </p>
            </div>

            {renderFieldRow(t('warehouse.modules.fieldRegistration.detail.fieldPickPriority', {}, 'Plukkprioritet'), 'pickPriorityNo', effectiveValues.pickPriorityNo, binType?.pickPriorityNo)}
            {renderFieldRow(t('warehouse.modules.fieldRegistration.detail.fieldPutAwayPriority', {}, 'Plasseringsprioritet'), 'putAwayPriorityNo', effectiveValues.putAwayPriorityNo, binType?.putAwayPriorityNo)}
            {renderFieldRow(t('warehouse.modules.fieldRegistration.detail.fieldSharedBin', {}, 'Delt lokasjon'), 'sharedBinYesNo', effectiveValues.sharedBinYesNo, binType?.sharedBinYesNo, true)}
            {renderFieldRow(t('warehouse.modules.fieldRegistration.detail.fieldAutoReplenish', {}, 'Auto-etterfylling'), 'autoReplenishment', effectiveValues.autoReplenishment, binType?.autoReplenishment, true)}
            {renderFieldRow(t('warehouse.modules.fieldRegistration.detail.fieldReplenishMin', {}, 'Min etterfylling'), 'replenishMinLevel', effectiveValues.replenishMinLevel, binType?.replenishMinLevel)}
            {renderFieldRow(t('warehouse.modules.fieldRegistration.detail.fieldReplenishMax', {}, 'Maks etterfylling'), 'replenishMaxLevel', effectiveValues.replenishMaxLevel, binType?.replenishMaxLevel)}
            {renderFieldRow(t('warehouse.modules.fieldRegistration.detail.fieldPickable', {}, 'Plukkbar'), 'pickableYesNo', effectiveValues.pickableYesNo, binType?.pickableYesNo, true)}
            {renderFieldRow(t('warehouse.modules.fieldRegistration.detail.fieldReservationAllowed', {}, 'Reservasjon tillatt'), 'reservationAllowed', effectiveValues.reservationAllowed, binType?.reservationAllowed, true)}

            {/* Edit / Save buttons */}
            <div className="flex items-center gap-2 pt-2">
              {binEditing ? (
                <>
                  <button
                    onClick={handleSaveBinProps}
                    disabled={saving}
                    className="flex-1 bg-[#0E7C86] hover:bg-[#0A5F66] text-white font-medium py-2 px-4 rounded-lg text-xs transition-colors min-h-[40px] flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
                    {t('common.buttons.save', {}, 'Lagre')}
                  </button>
                  <button
                    onClick={handleResetBinProps}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg text-xs transition-colors min-h-[40px] flex items-center justify-center gap-2"
                  >
                    <RotateCcw size={14} />
                    {t('common.buttons.cancel', {}, 'Avbryt')}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setBinEditing(true)}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg text-xs transition-colors min-h-[40px]"
                >
                  {t('warehouse.modules.fieldRegistration.detail.editProperties', {}, 'Rediger egenskaper')}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Extended Properties Section (New 5 fields) ──────────── */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <button
          onClick={() => setExtendedPropsExpanded(!extendedPropsExpanded)}
          className="w-full flex items-center justify-between p-4 text-left min-h-[44px]"
        >
          <div className="flex items-center gap-2">
            <Layers size={16} className="text-[#0E7C86]" />
            <h3 className="text-sm font-semibold text-gray-900">{t('warehouse.modules.fieldRegistration.detail.extendedConfigTitle', {}, 'Utvidet konfigurasjon')}</h3>
          </div>
          {extendedPropsExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
        </button>

        {extendedPropsExpanded && (
          <div className="px-4 pb-4 space-y-3">
            {/* ── A) Fast vare (Dedicated item) ──────────────── */}
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <Star size={14} className="text-amber-500" />
                <span className="text-xs font-semibold text-gray-800">{t('warehouse.modules.fieldRegistration.detail.dedicatedItem', {}, 'Fast vare for lokasjon')}</span>
              </div>

              {binEditing ? (
                <div className="space-y-2">
                  <div className="relative">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={binProps.itemNo || ''}
                        onChange={(e) => {
                          setBinProps(prev => ({ ...prev, itemNo: e.target.value }));
                          setItemSearchTerm(e.target.value);
                          setShowItemPicker(e.target.value.length > 0);
                        }}
                        onFocus={() => {
                          if (binProps.itemNo) {
                            setItemSearchTerm(binProps.itemNo);
                          }
                        }}
                        placeholder={t('warehouse.modules.fieldRegistration.detail.itemNoPlaceholder', {}, 'Skriv varenummer...')}
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-mono focus:border-[#0E7C86] focus:ring-1 focus:ring-[#0E7C86] min-h-[36px]"
                      />
                      {binProps.itemNo && (
                        <button
                          onClick={() => {
                            setBinProps(prev => ({ ...prev, itemNo: '' }));
                            setItemSearchTerm('');
                            setShowItemPicker(false);
                          }}
                          className="p-1.5 text-gray-400 hover:text-gray-600"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>

                    {/* Quick-select from items at this location */}
                    {showItemPicker && uniqueItemsFromBins.length > 0 && (
                      <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                        <div className="p-1.5 text-[10px] text-gray-400 font-medium border-b border-gray-100">
                          {t('warehouse.modules.fieldRegistration.detail.itemsAtLocation', {}, 'Varer på denne lokasjonen:')}
                        </div>
                        {uniqueItemsFromBins
                          .filter(i => !itemSearchTerm || i.itemNo.toLowerCase().includes(itemSearchTerm.toLowerCase()) || i.description.toLowerCase().includes(itemSearchTerm.toLowerCase()))
                          .map(item => (
                            <button
                              key={item.itemNo}
                              onClick={() => {
                                setBinProps(prev => ({ ...prev, itemNo: item.itemNo }));
                                setShowItemPicker(false);
                                setItemSearchTerm('');
                              }}
                              className="w-full text-left px-3 py-2 hover:bg-[#E0F2F4] text-xs flex items-center gap-2 min-h-[36px]"
                            >
                              <Package size={12} className="text-gray-400 flex-shrink-0" />
                              <span className="font-mono font-medium">{item.itemNo}</span>
                              <span className="text-gray-500 truncate">{item.description}</span>
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-600">
                    {bin.itemNo ? (
                      <span className="font-mono font-medium text-[#0E7C86]">{bin.itemNo}</span>
                    ) : (
                      <span className="text-gray-400 italic">{t('warehouse.modules.fieldRegistration.detail.noDedicatedItem', {}, 'Ingen fast vare')}</span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* ── B) Maks antall ulike varer ──────────────────── */}
            <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-gray-50">
              <div className="flex items-center gap-2">
                <Hash size={14} className="text-gray-400" />
                <span className="text-xs font-medium text-gray-700">{t('warehouse.modules.fieldRegistration.detail.maxDistinctItems', {}, 'Maks antall ulike varer')}</span>
              </div>
              <div className="flex-shrink-0 ml-2">
                {binEditing ? (
                  <input
                    type="number"
                    value={binProps.sharedBinMaxQuantity ?? ''}
                    onChange={(e) => setBinProps(prev => ({
                      ...prev,
                      sharedBinMaxQuantity: e.target.value ? Number(e.target.value) : undefined
                    }))}
                    className="w-24 px-2 py-1 border border-gray-300 rounded text-xs text-right focus:border-[#0E7C86] focus:ring-1 focus:ring-[#0E7C86] min-h-[32px]"
                    min={0}
                    placeholder="999999"
                  />
                ) : (
                  <span className="text-sm font-medium text-gray-600">
                    {bin.sharedBinMaxQuantity != null && bin.sharedBinMaxQuantity < 999999
                      ? bin.sharedBinMaxQuantity
                      : <span className="text-gray-400 italic text-xs">{t('warehouse.modules.fieldRegistration.detail.unlimited', {}, 'Ubegrenset')}</span>
                    }
                  </span>
                )}
              </div>
            </div>

            {/* ── D) Bevegelsestype (A/B/C/D-vare) - Dropdown ─── */}
            <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-gray-50">
              <div className="flex-1 min-w-0">
                <span className="text-xs font-medium text-gray-700">{t('warehouse.modules.fieldRegistration.detail.movementType', {}, 'Bevegelsestype')}</span>
                {bin.movementTypeNo && (
                  <span className="ml-1.5 text-[10px] text-[#0E7C86] font-medium">
                    ({getMovementTypeName(bin.movementTypeNo)})
                  </span>
                )}
              </div>
              <div className="flex-shrink-0 ml-2">
                {binEditing ? (
                  <select
                    value={binProps.movementTypeNo ?? ''}
                    onChange={(e) => setBinProps(prev => ({
                      ...prev,
                      movementTypeNo: e.target.value ? Number(e.target.value) : undefined
                    }))}
                    className="px-2 py-1 border border-gray-300 rounded text-xs focus:border-[#0E7C86] focus:ring-1 focus:ring-[#0E7C86] min-h-[32px] bg-white min-w-[140px]"
                  >
                    <option value="">{t('warehouse.modules.fieldRegistration.detail.selectPlaceholder', {}, '-- Velg --')}</option>
                    {movementTypes.map(mt => (
                      <option key={mt.movementTypeNo} value={mt.movementTypeNo}>
                        {mt.description1} ({mt.movementTypeNo})
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="text-sm font-medium text-gray-600">
                    {bin.movementTypeNo ? getMovementTypeName(bin.movementTypeNo) : <span className="text-gray-400 italic text-xs">{t('warehouse.modules.fieldRegistration.detail.notSet', {}, 'Ikke satt')}</span>}
                  </span>
                )}
              </div>
            </div>

            {/* ── E) Plukkrekkefølge (CustomSortID) ──────────── */}
            <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-gray-50">
              <div className="flex items-center gap-2">
                <Hash size={14} className="text-gray-400" />
                <span className="text-xs font-medium text-gray-700">{t('warehouse.modules.fieldRegistration.detail.pickSortOrder', {}, 'Plukkrekkefølge')}</span>
              </div>
              <div className="flex-shrink-0 ml-2">
                {binEditing ? (
                  <input
                    type="number"
                    value={binProps.customSortID ?? ''}
                    onChange={(e) => setBinProps(prev => ({
                      ...prev,
                      customSortID: e.target.value ? Number(e.target.value) : undefined
                    }))}
                    className="w-24 px-2 py-1 border border-gray-300 rounded text-xs text-right focus:border-[#0E7C86] focus:ring-1 focus:ring-[#0E7C86] min-h-[32px]"
                    min={0}
                    placeholder="1000"
                  />
                ) : (
                  <span className="text-sm font-medium text-gray-600">
                    {bin.customSortID ?? <span className="text-gray-400 italic text-xs">{t('warehouse.modules.fieldRegistration.detail.standard', {}, 'Standard')}</span>}
                  </span>
                )}
              </div>
            </div>

            {/* ── F) Lokasjonsgruppe (BinGrpNo) - Dropdown ────── */}
            <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-gray-50">
              <div className="flex-1 min-w-0">
                <span className="text-xs font-medium text-gray-700">{t('warehouse.modules.fieldRegistration.detail.binGroup', {}, 'Lokasjonsgruppe')}</span>
                {bin.binGrpNo && (
                  <span className="ml-1.5 text-[10px] text-[#0E7C86] font-medium">
                    ({getBinGroupName(bin.binGrpNo)})
                  </span>
                )}
              </div>
              <div className="flex-shrink-0 ml-2">
                {binEditing ? (
                  <select
                    value={binProps.binGrpNo ?? ''}
                    onChange={(e) => setBinProps(prev => ({
                      ...prev,
                      binGrpNo: e.target.value ? Number(e.target.value) : undefined
                    }))}
                    className="px-2 py-1 border border-gray-300 rounded text-xs focus:border-[#0E7C86] focus:ring-1 focus:ring-[#0E7C86] min-h-[32px] bg-white min-w-[140px]"
                  >
                    <option value="">{t('warehouse.modules.fieldRegistration.detail.selectPlaceholder', {}, '-- Velg --')}</option>
                    {binGroups.map(grp => (
                      <option key={grp.binGrpNo} value={grp.binGrpNo}>
                        {grp.description1} ({grp.binGrpNo})
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="text-sm font-medium text-gray-600">
                    {bin.binGrpNo ? getBinGroupName(bin.binGrpNo) : <span className="text-gray-400 italic text-xs">{t('warehouse.modules.fieldRegistration.detail.notSet', {}, 'Ikke satt')}</span>}
                  </span>
                )}
              </div>
            </div>

            {/* Quick-apply buttons for movement type (only in edit mode) */}
            {binEditing && movementTypes.length > 0 && (
              <div className="pt-1">
                <span className="text-[10px] text-gray-500 font-medium block mb-1.5">{t('warehouse.modules.fieldRegistration.detail.quickSelectMovementType', {}, 'Hurtigvalg bevegelsestype:')}</span>
                <div className="flex flex-wrap gap-1.5">
                  {movementTypes.map(mt => (
                    <button
                      key={mt.movementTypeNo}
                      onClick={() => setBinProps(prev => ({ ...prev, movementTypeNo: mt.movementTypeNo }))}
                      className={`px-3 py-1.5 rounded-full text-[10px] font-medium transition-colors min-h-[28px] ${
                        binProps.movementTypeNo === mt.movementTypeNo
                          ? 'bg-[#0E7C86] text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {mt.description1}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quick-apply buttons for bin group (only in edit mode) */}
            {binEditing && binGroups.length > 0 && (
              <div className="pt-1">
                <span className="text-[10px] text-gray-500 font-medium block mb-1.5">{t('warehouse.modules.fieldRegistration.detail.quickSelectBinGroup', {}, 'Hurtigvalg lokasjonsgruppe:')}</span>
                <div className="flex flex-wrap gap-1.5">
                  {binGroups.map(grp => (
                    <button
                      key={grp.binGrpNo}
                      onClick={() => setBinProps(prev => ({ ...prev, binGrpNo: grp.binGrpNo }))}
                      className={`px-3 py-1.5 rounded-full text-[10px] font-medium transition-colors min-h-[28px] ${
                        binProps.binGrpNo === grp.binGrpNo
                          ? 'bg-[#0E7C86] text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {grp.description1}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Edit / Save buttons for extended props (shared with main section) */}
            <div className="flex items-center gap-2 pt-2">
              {binEditing ? (
                <>
                  <button
                    onClick={handleSaveBinProps}
                    disabled={saving}
                    className="flex-1 bg-[#0E7C86] hover:bg-[#0A5F66] text-white font-medium py-2 px-4 rounded-lg text-xs transition-colors min-h-[40px] flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
                    {t('warehouse.modules.fieldRegistration.detail.saveAll', {}, 'Lagre alle')}
                  </button>
                  <button
                    onClick={handleResetBinProps}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg text-xs transition-colors min-h-[40px] flex items-center justify-center gap-2"
                  >
                    <RotateCcw size={14} />
                    {t('common.buttons.cancel', {}, 'Avbryt')}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setBinEditing(true)}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg text-xs transition-colors min-h-[40px]"
                >
                  {t('warehouse.modules.fieldRegistration.detail.editConfig', {}, 'Rediger konfigurasjon')}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Items & Capacity Section ────────────────────────────── */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <button
          onClick={() => setItemsExpanded(!itemsExpanded)}
          className="w-full flex items-center justify-between p-4 text-left min-h-[44px]"
        >
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-gray-900">{t('warehouse.modules.fieldRegistration.detail.itemsCapacityTitle', {}, 'Varer og kapasitet')}</h3>
            <span className="text-xs text-gray-500">
              ({completionStatus.itemsWithCapacity}/{completionStatus.totalItems})
            </span>
          </div>
          {itemsExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
        </button>

        {itemsExpanded && (
          <div className="px-4 pb-4 space-y-2">
            {items.length === 0 ? (
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <Package size={20} className="mx-auto mb-2 text-gray-400" />
                <p className="text-xs text-gray-500">{t('warehouse.modules.fieldRegistration.detail.noItemsRegistered', {}, 'Ingen varer registrert på denne lokasjonen')}</p>
              </div>
            ) : (
              items.map((item) => {
                const isEditing = editingItemNo === item.itemNo;
                const hasStdBinCode = item.stdBinCode && item.stdBinCode !== '0' && item.stdBinCode.trim() !== '';
                const isThisLocationStd = hasStdBinCode && item.stdBinCode === bin.binCode;

                return (
                  <div
                    key={item.itemNo}
                    className={`rounded-lg border p-3 transition-colors ${
                      item.capacityConfigured
                        ? 'bg-green-50 border-green-200'
                        : 'bg-amber-50 border-amber-200'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Package size={12} className={item.capacityConfigured ? 'text-green-600' : 'text-amber-600'} />
                          <span className="text-xs font-bold text-gray-900 truncate">{item.itemNo}</span>
                          {item.capacityConfigured ? (
                            <CheckCircle size={12} className="text-green-500 flex-shrink-0" />
                          ) : (
                            <AlertTriangle size={12} className="text-amber-500 flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-[10px] text-gray-500 truncate mt-0.5">{item.description1}</p>
                      </div>

                      {!isEditing && (
                        <button
                          onClick={() => handleStartEditItem(item)}
                          className="text-xs text-[#0E7C86] hover:text-[#0A5F66] font-medium ml-2 min-h-[32px] px-2"
                        >
                          {t('common.buttons.edit', {}, 'Rediger')}
                        </button>
                      )}
                    </div>

                    {/* Current values (view mode) */}
                    {!isEditing && (
                      <div className="space-y-1 mt-2">
                        <div className="flex items-center gap-4 text-[10px] text-gray-600">
                          <span>{t('warehouse.modules.fieldRegistration.detail.itemQtyLabel', {}, 'Antall:')} <b>{item.currentQuantity}</b></span>
                          <span>{t('warehouse.modules.fieldRegistration.detail.itemMaxLabel', {}, 'Maks:')} <b>{item.itemBinSize ? (item.itemBinSize.maxQuantity >= 999999 ? t('warehouse.modules.fieldRegistration.detail.notSet', {}, 'Ikke satt') : item.itemBinSize.maxQuantity) : t('warehouse.modules.fieldRegistration.detail.missingValue', {}, 'Mangler')}</b></span>
                          <span>{t('warehouse.modules.fieldRegistration.detail.itemPutAwayPriLabel', {}, 'Plassering-pri:')} <b>{item.itemBinSize?.putAwayPriorityNo ?? '-'}</b></span>
                        </div>

                        {/* C) StdBinCode display + quick-action */}
                        <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-gray-200/50">
                          <div className="flex items-center gap-1.5 text-[10px]">
                            <MapPin size={10} className="text-gray-400" />
                            <span className="text-gray-500">{t('warehouse.modules.fieldRegistration.detail.fixedPickLocation', {}, 'Fast plukklokasjon:')}</span>
                            {hasStdBinCode ? (
                              <span className={`font-mono font-medium ${isThisLocationStd ? 'text-green-600' : 'text-gray-700'}`}>
                                {item.stdBinCode}
                                {isThisLocationStd && <CheckCircle size={10} className="inline ml-1 text-green-500" />}
                              </span>
                            ) : (
                              <span className="text-gray-400 italic">{t('warehouse.modules.fieldRegistration.detail.notSet', {}, 'Ikke satt')}</span>
                            )}
                          </div>

                          {/* Quick-action: Set this location as StdBinCode */}
                          {!isThisLocationStd && (
                            <button
                              onClick={() => handleSetStdBinCode(item.itemNo)}
                              disabled={saving}
                              className="text-[10px] text-[#0E7C86] hover:text-[#0A5F66] font-medium px-2 py-1 rounded bg-[#E0F2F4] hover:bg-[#C5E8EC] transition-colors min-h-[24px] flex items-center gap-1 disabled:opacity-50"
                            >
                              <MapPin size={10} />
                              {t('warehouse.modules.fieldRegistration.detail.setThisLocation', {}, 'Sett denne lokasjonen')}
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Edit mode */}
                    {isEditing && (
                      <div className="mt-2 space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] text-gray-500 block mb-0.5">{t('warehouse.modules.fieldRegistration.detail.maxQuantityLabel', {}, 'Maks antall')}</label>
                            <input
                              type="number"
                              value={editCapacity.maxQuantity}
                              onChange={(e) => setEditCapacity(prev => ({
                                ...prev,
                                maxQuantity: Number(e.target.value) || 0
                              }))}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:border-[#0E7C86] focus:ring-1 focus:ring-[#0E7C86] min-h-[36px]"
                              min={0}
                              autoFocus
                            />
                          </div>
                          <div>
                            <label className="text-[10px] text-gray-500 block mb-0.5">{t('warehouse.modules.fieldRegistration.detail.putAwayPriorityLabel', {}, 'Plasseringspr.')}</label>
                            <input
                              type="number"
                              value={editCapacity.putAwayPriorityNo}
                              onChange={(e) => setEditCapacity(prev => ({
                                ...prev,
                                putAwayPriorityNo: Number(e.target.value) || 0
                              }))}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:border-[#0E7C86] focus:ring-1 focus:ring-[#0E7C86] min-h-[36px]"
                              min={0}
                            />
                          </div>
                        </div>

                        {/* C) StdBinCode in edit mode */}
                        <div className="flex items-center justify-between pt-1 pb-1">
                          <div className="flex items-center gap-1.5 text-[10px]">
                            <MapPin size={10} className="text-gray-400" />
                            <span className="text-gray-500">{t('warehouse.modules.fieldRegistration.detail.fixedPickLocation', {}, 'Fast plukklokasjon:')}</span>
                            <span className="font-mono font-medium text-gray-700">
                              {(item.stdBinCode && item.stdBinCode !== '0' && item.stdBinCode.trim() !== '') ? item.stdBinCode : t('warehouse.modules.fieldRegistration.detail.notSet', {}, 'Ikke satt')}
                            </span>
                          </div>
                          {!(item.stdBinCode === bin.binCode) && (
                            <button
                              onClick={() => handleSetStdBinCode(item.itemNo)}
                              disabled={saving}
                              className="text-[10px] text-[#0E7C86] hover:text-[#0A5F66] font-medium px-2 py-1 rounded bg-[#E0F2F4] hover:bg-[#C5E8EC] transition-colors min-h-[24px] flex items-center gap-1 disabled:opacity-50"
                            >
                              <MapPin size={10} />
                              {t('warehouse.modules.fieldRegistration.detail.setThis', {}, 'Sett denne')}
                            </button>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleSaveItemCapacity(item)}
                            disabled={saving}
                            className="flex-1 bg-[#0E7C86] hover:bg-[#0A5F66] text-white font-medium py-1.5 px-3 rounded text-xs min-h-[36px] flex items-center justify-center gap-1 disabled:opacity-50"
                          >
                            {saving ? <Loader size={12} className="animate-spin" /> : <Save size={12} />}
                            {t('common.buttons.save', {}, 'Lagre')}
                          </button>
                          <button
                            onClick={handleBulkApply}
                            disabled={saving}
                            className="flex-1 bg-[#F58220] hover:bg-[#E07010] text-white font-medium py-1.5 px-3 rounded text-xs min-h-[36px] flex items-center justify-center gap-1 disabled:opacity-50"
                            title={t('warehouse.modules.fieldRegistration.detail.bulkApplyTitle', {}, 'Bruk samme verdier for alle varer uten kapasitet')}
                          >
                            <Copy size={12} />
                            {t('warehouse.modules.fieldRegistration.detail.bulkApply', {}, 'Bruk for lignende')}
                          </button>
                          <button
                            onClick={() => setEditingItemNo(null)}
                            className="bg-gray-100 hover:bg-gray-200 text-gray-600 font-medium py-1.5 px-3 rounded text-xs min-h-[36px]"
                          >
                            {t('common.buttons.cancel', {}, 'Avbryt')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};
